import { useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { fmt } from '@cca/ui';
import { yearLabel } from '../lib/format';
import type { DragSystem } from '../lib/dragSystem';
import type { CpiDefaults, ProjectedLine } from '../lib/projection';
import type { ProjectGroup } from '../lib/totals';
import type { LineItem, Project } from '../types';
import { ArchivedRow } from './ArchivedRow';
import { BudgetRow, hasLineCustomisation } from './BudgetRow';
import { NetRow } from './NetRow';
import { TombstoneIcon } from './TombstoneIcon';
import { useIsViewer } from '../lib/viewerContext';

const COL_COUNT = 11;

const sumActuals = (rows: { code: string }[], actualByCode: Map<string, number>): number =>
  rows.reduce((s, r) => s + (actualByCode.get(r.code) ?? 0), 0);

type Kind = 'income' | 'expense';

type Props = {
  group: ProjectGroup;
  defaults: CpiDefaults;
  baseYear: number;
  projects: Project[];
  hidden: string[];
  showHidden: boolean;
  showArchived: boolean;
  isFirst: boolean;
  isLast: boolean;
  startingLineNo: number;
  dragSystem: DragSystem;
  priorPeriod: string;
  actualByCode: Map<string, number>;
  quotaBoundLineCode?: string | null;
  onRename: (id: string, name: string) => void;
  onMove: (id: string, direction: 1 | -1) => void;
  onDelete: (id: string) => void;
  onToggleArchived: (id: string) => void;
  onAddRow: (kind: Kind, projectId: string) => void;
  onLineUpdate: (kind: Kind) => (id: string, patch: Partial<LineItem>) => void;
  onLineDelete: (kind: Kind) => (id: string) => void;
  onLineToggleHidden: (id: string) => void;
  onLineAssignProject: (kind: Kind) => (id: string, projectId: string | null) => void;
  onLineRestore: (kind: Kind, id: string) => void;
};

export function ProjectBlock({
  group,
  defaults,
  baseYear,
  projects,
  hidden,
  showHidden,
  showArchived,
  isFirst,
  isLast,
  startingLineNo,
  dragSystem,
  priorPeriod,
  actualByCode,
  quotaBoundLineCode,
  onRename,
  onMove,
  onDelete,
  onToggleArchived,
  onAddRow,
  onLineUpdate,
  onLineDelete,
  onLineToggleHidden,
  onLineAssignProject,
  onLineRestore,
}: Props) {
  const isViewer = useIsViewer();
  const { project } = group;
  const [collapsed, setCollapsed] = useState(false);

  const visible = (items: ProjectedLine[]) =>
    items.filter((i) => showHidden || !hidden.includes(i.id));

  const visibleIncome = visible(group.income);
  const visibleExpenses = visible(group.expenses);
  const archivedCount = group.archivedIncome.length + group.archivedExpenses.length;
  const netActual =
    sumActuals(visibleIncome, actualByCode) - sumActuals(visibleExpenses, actualByCode);

  // Pre-compute starting numbers per subsection — avoids stateful
  // closures that React StrictMode would invoke twice.
  const incomeStartingNumber = startingLineNo;
  const expenseStartingNumber = startingLineNo + visibleIncome.length;

  // Activity-level customisation flag — same gold dot shows on the
  // collapsed project chevron when any of its rows has been edited.
  const projectHasCustomisation =
    visibleIncome.some(hasLineCustomisation) || visibleExpenses.some(hasLineCustomisation);

  if (collapsed) {
    // Collapsed view mirrors the operating row-category collapsed
    // styling: chevron + project name + bold blue Net values across
    // the four years, all in a single header-style row.
    return (
      <div className={`project-block ${isFirst ? 'first' : ''}`}>
        <table className="budget-table">
          <colgroup>
            <col className="w-6 print-collapse" />
            <col className="w-8 print-collapse" />
            <col className="w-10 col-no" />
            <col className="w-20 col-code" />
            <col className="col-name" />
            <col className="w-24 col-actual" />
            <col className="w-28 col-proj" />
            <col className="w-28 col-y1" />
            <col className="w-28 col-y2" />
            <col className="w-28 col-y3" />
            <col className="w-20 print-collapse" />
          </colgroup>
          <tbody>
            <tr className="row-category">
              <td colSpan={5}>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCollapsed(false)}
                    className={`row-control flex-shrink-0 no-print ${projectHasCustomisation ? 'has-detail' : ''}`}
                    title={
                      projectHasCustomisation
                        ? 'Expand — contains rows with notes / CPI overrides / amount overrides'
                        : 'Expand activity'
                    }
                    aria-label="Expand activity"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <span className="text-[15px] font-bold text-ccaanz-blue">{project.name}</span>
                </div>
              </td>
              <td className="col-actual text-right num text-[15px] font-bold">{fmt(netActual)}</td>
              <td className="col-current text-right num text-[15px] font-bold">
                {fmt(group.net.current)}
              </td>
              <td className="col-shade text-right num text-[15px] font-bold">
                {fmt(group.net.y1)}
              </td>
              <td className="text-right num text-[15px] font-bold">{fmt(group.net.y2)}</td>
              <td className="col-shade text-right num text-[15px] font-bold">
                {fmt(group.net.y3)}
              </td>
              <td className="no-print"></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={`project-block ${isFirst ? 'first' : ''}`}>
      <div className="project-header">
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="text-ccaanz-grey hover:text-ccaanz-blue flex-shrink-0 no-print"
          title="Collapse activity"
          aria-label="Collapse activity"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
        <input
          className="input-project"
          value={project.name}
          readOnly={isViewer}
          onChange={(e) => onRename(project.id, e.target.value)}
          aria-label="Project name"
        />
        <div className="flex items-center gap-2 no-print">
          {archivedCount > 0 && (
            <button
              type="button"
              onClick={() => onToggleArchived(project.id)}
              className="pill pill-brand cursor-pointer"
              title={
                showArchived
                  ? `Hide the ${archivedCount} archived row${archivedCount > 1 ? 's' : ''}`
                  : `Reveal ${archivedCount} archived row${archivedCount > 1 ? 's' : ''} in this project`
              }
            >
              <TombstoneIcon className="w-3 h-3" />
              {archivedCount} archived
            </button>
          )}
          {!isViewer && (
            <>
              <button
                type="button"
                className="row-control"
                onClick={() => onMove(project.id, -1)}
                disabled={isFirst}
                title="Move project up"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="row-control"
                onClick={() => onMove(project.id, 1)}
                disabled={isLast}
                title="Move project down"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="row-control danger"
                onClick={() => onDelete(project.id)}
                title="Delete project (line items return to operating)"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      <table className="budget-table">
        <colgroup>
          <col className="w-6 print-collapse" />
          <col className="w-8 print-collapse" />
          <col className="w-10 col-no" />
          <col className="w-20 col-code" />
          <col className="col-name" />
          <col className="w-24 col-actual" />
          <col className="w-28 col-proj" />
          <col className="w-28 col-y1" />
          <col className="w-28 col-y2" />
          <col className="w-28 col-y3" />
          <col className="w-20 print-collapse" />
        </colgroup>
        <thead>
          <tr>
            <th></th>
            <th className="no-print"></th>
            <th className="col-no">#</th>
            <th className="col-code">Code</th>
            <th className="col-name">Account</th>
            <th className="right col-actual" title="Imported from Xero P&L">
              FY{priorPeriod} Actual
            </th>
            <th className="right col-current">Proj {yearLabel(baseYear, 0)}</th>
            <th className="right col-shade col-y1">{yearLabel(baseYear, 1)}</th>
            <th className="right col-y2">{yearLabel(baseYear, 2)}</th>
            <th className="right col-shade col-y3">{yearLabel(baseYear, 3)}</th>
            <th className="no-print"></th>
          </tr>
        </thead>
        <tbody>
          <Subsection
            label="Income"
            kind="income"
            items={visibleIncome}
            archived={showArchived ? group.archivedIncome : []}
            project={project}
            onAddRow={onAddRow}
            startingNumber={incomeStartingNumber}
            defaults={defaults}
            projects={projects}
            hidden={hidden}
            dragSystem={dragSystem}
            actualByCode={actualByCode}
            quotaBoundLineCode={quotaBoundLineCode ?? null}
            onLineUpdate={onLineUpdate('income')}
            onLineDelete={onLineDelete('income')}
            onLineToggleHidden={onLineToggleHidden}
            onLineAssignProject={onLineAssignProject('income')}
            onLineRestore={onLineRestore}
          />
          <Subsection
            label="Expenses"
            kind="expense"
            items={visibleExpenses}
            archived={showArchived ? group.archivedExpenses : []}
            project={project}
            onAddRow={onAddRow}
            startingNumber={expenseStartingNumber}
            defaults={defaults}
            projects={projects}
            hidden={hidden}
            dragSystem={dragSystem}
            actualByCode={actualByCode}
            quotaBoundLineCode={quotaBoundLineCode ?? null}
            onLineUpdate={onLineUpdate('expense')}
            onLineDelete={onLineDelete('expense')}
            onLineToggleHidden={onLineToggleHidden}
            onLineAssignProject={onLineAssignProject('expense')}
            onLineRestore={onLineRestore}
          />
          <NetRow label={`Net — ${project.name}`} values={group.net} actual={netActual} />
        </tbody>
      </table>
    </div>
  );
}

function Subsection({
  label,
  kind,
  items,
  archived,
  project,
  onAddRow,
  startingNumber,
  defaults,
  projects,
  hidden,
  dragSystem,
  actualByCode,
  quotaBoundLineCode,
  onLineUpdate,
  onLineDelete,
  onLineToggleHidden,
  onLineAssignProject,
  onLineRestore,
}: {
  label: string;
  kind: Kind;
  items: ProjectedLine[];
  archived: LineItem[];
  project: Project;
  onAddRow: (kind: Kind, projectId: string) => void;
  startingNumber: number;
  defaults: CpiDefaults;
  projects: Project[];
  hidden: string[];
  dragSystem: DragSystem;
  actualByCode: Map<string, number>;
  quotaBoundLineCode?: string | null;
  onLineUpdate: (id: string, patch: Partial<LineItem>) => void;
  onLineDelete: (id: string) => void;
  onLineToggleHidden: (id: string) => void;
  onLineAssignProject: (id: string, projectId: string | null) => void;
  onLineRestore: (kind: Kind, id: string) => void;
}) {
  const projectDrop = dragSystem.projectHandlers(kind, project.id);
  const isProjectTarget = dragSystem.isProjectTarget(kind, project.id);
  return (
    <>
      <tr className={`row-subhead ${isProjectTarget ? 'drop-section' : ''}`} {...projectDrop}>
        <td colSpan={COL_COUNT}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="label">{label}</span>
            <SubsectionAddButton onAdd={() => onAddRow(kind, project.id)} label={label} />
          </div>
        </td>
      </tr>

      {items.length === 0 && archived.length === 0 ? (
        <tr className="row-empty">
          <td colSpan={COL_COUNT}>No {label.toLowerCase()} lines yet.</td>
        </tr>
      ) : (
        items.map((row, idx) => (
          <BudgetRow
            key={row.id}
            row={row}
            lineNo={startingNumber + idx}
            defaults={defaults}
            projects={projects}
            isHidden={hidden.includes(row.id)}
            kind={kind}
            dragSystem={dragSystem}
            actual={actualByCode.get(row.code) ?? null}
            lockY1={!!quotaBoundLineCode && row.code === quotaBoundLineCode}
            onUpdate={onLineUpdate}
            onDelete={onLineDelete}
            onToggleHidden={onLineToggleHidden}
            onAssignProject={onLineAssignProject}
          />
        ))
      )}

      {archived.map((row) => (
        <ArchivedRow key={row.id} row={row} kind={kind} onRestore={onLineRestore} />
      ))}
    </>
  );
}

function SubsectionAddButton({ onAdd, label }: { onAdd: () => void; label: string }) {
  const isViewer = useIsViewer();
  if (isViewer) return null;
  return (
    <button type="button" onClick={onAdd} className="no-print btn btn-ghost btn-sm">
      <Plus className="w-3 h-3" />
      Add {label.toLowerCase()} row
    </button>
  );
}
